import type { Column } from '@tanstack/react-table';
import { useMemo } from 'react';

export interface UniqueValue {
  value: unknown;
  label: string;
}

export function useUniqueColumnValues<TData>(
  column: Column<TData, unknown>,
  data: TData[]
): UniqueValue[] {
  return useMemo(() => {
    const meta = column.columnDef.meta;
    const getFilterValue = meta?.getFilterValue;
    const accessorFn = column.accessorFn;

    if (!getFilterValue && !accessorFn) return [];

    const uniqueValues = new Map<unknown, string>();

    for (const row of data) {
      const rawValue = getFilterValue ? getFilterValue(row) : accessorFn?.(row, 0);
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];

      for (const value of values) {
        if (value !== null && value !== undefined && !uniqueValues.has(value)) {
          uniqueValues.set(value, String(value));
        }
      }
    }

    return Array.from(uniqueValues.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [column, data]);
}
